import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { NeueLoadingSpinner } from '../components/loading/spinner';
import injectScript from 'src/utils/document/inject-script';
import { NeueButtonBase } from '../components/neue-button/base';
import { apiUploadContent } from 'src/utils/journeyApi';
import DropboxIntegrationIcon from 'src/monet/icons/DropboxIntegrationIcon';
import { useCurrentSubscription } from 'src/store/subscription';
import OrganizationLink from 'src/common/OrganizationLink';

type Props = {
  onNewUrl: (url: string, title: string) => void;
  setBlockToLoading: () => void;
};

export const IntegrationDropboxPanel = ({ onNewUrl, setBlockToLoading }: Props) => {
  const { isEnterprise, isOnTrial } = useCurrentSubscription(state => state);
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    injectScript('https://www.dropbox.com/static/api/2/dropins.js', 'dropboxjs', {
      'data-app-key': 'c36lxnttqb317pi',
    }).then(() => {
      setLoaded(true);
    }).catch(() => {
      setLoaded(false);
      setHasError(true);
    })
  }, []);

  if (!isEnterprise && !isOnTrial) {
    return (
      <div className='flex flex-col space-y-4 text-neue-journey-fg'>
        <div className='text-neue-journey-small'>
          Import content from your Dropbox into Journey. Upgrade to Enterprise plan to use Dropbox Integration
        </div>
        <OrganizationLink to='/billing'>
          <button className='btn-bedrock-secondary self-start flex space-x-1'>
            <span>Upgrade to Enterprise</span>
          </button>
        </OrganizationLink>
      </div>
    );
  }

  const dropboxOptions = {

    // Required. Called when a user selects an item in the Chooser.
    success: async function (files: any) {
      const file = files[0];
      const link = file.link;
      const name = file.name;

      setBlockToLoading();      
      const { url } = await apiUploadContent(link);      
      onNewUrl(url, name);
    },

    // Optional. Called when the user closes the dialog without selecting a file
    // and does not include any parameters.
    cancel: function () {

    },

    // Optional. "preview" (default) is a preview link to the document for sharing,
    // "direct" is an expiring link to download the contents of the file. For more
    // information about link types, see Link types below.
    linkType: "direct", // or "direct"

    // Optional. A value of false (default) limits selection to a single file, while
    // true enables multiple file selection.
    multiselect: false, // or true

    // Optional. This is a list of file extensions. If specified, the user will
    // only be able to select files with these extensions. You may also specify
    // file types, such as "video" or "images" in the list. For more information,
    // see File types below. By default, all extensions are allowed.

    // Optional. A value of false (default) limits selection to files,
    // while true allows the user to select both folders and files.
    // You cannot specify `linkType: "direct"` when using `folderselect: true`.
    folderselect: false, // or true
  };

  const onClickChooser = () => {
    // @ts-ignore
    if (window.Dropbox) {
      // @ts-ignore
      window.Dropbox.choose(dropboxOptions);
    }    
  }

  const renderButton = () => {
    if (!loaded && !hasError) {
      return (
        <div className="h-[40px] w-full flex items-center justify-center">
          <NeueLoadingSpinner theme='journey' size='16' className='mx-auto' />
        </div>
      )
    } else if (!loaded && hasError) {
      return (
        <div className="text-neue-journey-small text-red">
          Error loading the picker. Please turn off any ad-blocker you might have in your browser.
        </div>
      )
    }

    return (
      <NeueButtonBase
        onClick={onClickChooser}
        className={classNames('py-[9px] px-4 text-neue-journey-fg')}
        baseColor={'rgb(var(--neue-journey-fg-10))'}
        gradientMaxOpacity={0.1}
      >
        <div className="flex items-center space-x-2">
          <DropboxIntegrationIcon className="w-5 h-5" />
          <div>Pick from Dropbox</div>
        </div>
      </NeueButtonBase>
    )
  }

  return (
    <div className='flex flex-col space-y-4 text-neue-journey-fg'>
      <div className='text-neue-journey-small'>
        Import content from your Dropbox into Journey
      </div>
      {renderButton()}
    </div>
  );
};
